// Postgres-backed Read API helper for engagement_index_* collections.
// Mirrors pgReadApi.ts (active_users). Returns a mock Firestore snapshot
// { forEach, docs, empty } on success, or null on error / unauthenticated.
// Use emptySnap as the || fallback instead of Firestore — no Firestore fallback.
import firebase from 'firebase/app';
import 'firebase/auth';
import axios from 'axios';
import { decodeSessionUser } from './core/cacheService';

const ENG_API = 'https://us-central1-people2-admin.cloudfunctions.net/engagementReadApi';

// The app logs in via signInWithCustomToken, so the Firebase ID token carries no
// `email` claim. engagementReadApi resolves the caller's company from the email, so we
// pass the logged-in email (from the persisted session) as ?caller=. The token is still
// verified server-side — this only supplies the identity the token omits.
function callerEmail(): string | null {
  try {
    const u = decodeSessionUser(localStorage.getItem('userData'));
    return u && u.email ? String(u.email) : null;
  } catch (e) {
    return null;
  }
}

// axios instance that stamps ?caller=<email> onto every engagementReadApi request.
const pgClient = axios.create();
pgClient.interceptors.request.use((config: any) => {
  const caller = callerEmail();
  if (caller) config.params = { ...(config.params || {}), caller };
  return config;
});

type MockSnap = {
  forEach: (cb: (doc: { data: () => any }) => void) => void;
  docs: { data: () => any }[];
  empty: boolean;
};

// Empty snapshot — use as || fallback when PG returns null.
// forEach does nothing so processing loops produce empty results gracefully.
export const emptySnap: MockSnap = { forEach: () => {}, docs: [], empty: true };

async function authHeaders(): Promise<Record<string, string> | null> {
  const cu = firebase.auth().currentUser;
  if (!cu) return null;
  const token = await cu.getIdToken();
  return { Authorization: `Bearer ${token}` };
}

function makeSnap(rows: any[]): MockSnap {
  const docs = rows.map((row) => ({ data: () => row }));
  return {
    docs,
    empty: rows.length === 0,
    forEach: (cb) => docs.forEach(cb),
  };
}

// GET /quarter — returns mock snapshot or null
export async function pgQuarter(params: {
  company: string;
  quarter?: number;
  scope_type?: string;
  scope_id?: string;
  type?: string;
}): Promise<MockSnap | null> {
  try {
    const headers = await authHeaders();
    if (!headers) return null;
    const p: any = { company: params.company };
    if (params.quarter) p.quarter = params.quarter;
    if (params.scope_type) p.scope_type = params.scope_type;
    if (params.scope_id) p.scope_id = params.scope_id;
    if (params.type) p.type = params.type;
    const res = await pgClient.get(`${ENG_API}/quarter`, { params: p, headers, timeout: 15000 });
    return makeSnap((res.data && res.data.rows) || []);
  } catch (e) {
    console.error('[engagementPgApi] pgQuarter failed:', e);
    return null;
  }
}

// GET /rolling/company?date_gte&date_lte — returns mock snapshot or null
export async function pgRollingCompanyRange(params: {
  company: string;
  window: number;
  date_gte: string;
  date_lte: string;
}): Promise<MockSnap | null> {
  try {
    const headers = await authHeaders();
    if (!headers) return null;
    const res = await pgClient.get(`${ENG_API}/rolling/company`, {
      params: { company: params.company, window: params.window, date_gte: params.date_gte, date_lte: params.date_lte },
      headers,
      timeout: 15000,
    });
    return makeSnap((res.data && res.data.rows) || []);
  } catch (e) {
    console.error('[engagementPgApi] pgRollingCompanyRange failed:', e);
    return null;
  }
}

// GET /rolling/scope?date — all scopes for a point date — returns mock snapshot or null
export async function pgRollingScopeDate(params: {
  company: string;
  window: number;
  date: string;
  scope_type?: string;
  scope_id?: string;
}): Promise<MockSnap | null> {
  try {
    const headers = await authHeaders();
    if (!headers) return null;
    const p: any = { company: params.company, window: params.window, date: params.date };
    if (params.scope_type) p.scope_type = params.scope_type;
    if (params.scope_id) p.scope_id = params.scope_id;
    const res = await pgClient.get(`${ENG_API}/rolling/scope`, { params: p, headers, timeout: 15000 });
    return makeSnap((res.data && res.data.rows) || []);
  } catch (e) {
    console.error('[engagementPgApi] pgRollingScopeDate failed:', e);
    return null;
  }
}

// GET /rolling/scope?latest=1 — latest row (all scopes) — returns mock snapshot or null
export async function pgRollingScopeLatest(params: {
  company: string;
  window: number;
}): Promise<MockSnap | null> {
  try {
    const headers = await authHeaders();
    if (!headers) return null;
    const res = await pgClient.get(`${ENG_API}/rolling/scope`, {
      params: { company: params.company, window: params.window, latest: 1 },
      headers,
      timeout: 15000,
    });
    const row = res.data && res.data.row;
    return makeSnap(row ? [row] : []);
  } catch (e) {
    console.error('[engagementPgApi] pgRollingScopeLatest failed:', e);
    return null;
  }
}

// GET /rolling/scope?scope_type&scope_id&date_gte&date_lte — range with scope filter
export async function pgRollingScopeRange(params: {
  company: string;
  window: number;
  scope_type: string;
  scope_id: string;
  date_gte: string;
  date_lte: string;
}): Promise<MockSnap | null> {
  try {
    const headers = await authHeaders();
    if (!headers) return null;
    const res = await pgClient.get(`${ENG_API}/rolling/scope`, {
      params: {
        company: params.company,
        window: params.window,
        scope_type: params.scope_type,
        scope_id: params.scope_id,
        date_gte: params.date_gte,
        date_lte: params.date_lte,
      },
      headers,
      timeout: 15000,
    });
    return makeSnap((res.data && res.data.rows) || []);
  } catch (e) {
    console.error('[engagementPgApi] pgRollingScopeRange failed:', e);
    return null;
  }
}

// GET /day — engagement_index_day_1row mirror served from Postgres. Returns a record
// keyed `<company>-<email>-<type>-<date>` matching cacheService.getEngagementIndexDay's
// processed output (so call sites can swap with no downstream changes); null on error
// so callers can fall back to the legacy CF (`getengagementindexday1row`).
export async function pgEngagementIndexDay(
  company: string,
  dateGte: string,
  dateLte: string,
): Promise<Record<string, any> | null> {
  try {
    const headers = await authHeaders();
    if (!headers) return null;
    const res = await pgClient.get(`${ENG_API}/day`, {
      params: { company, date_gte: dateGte, date_lte: dateLte },
      headers,
      timeout: 15000,
    });
    const rows: any[] = (res.data && res.data.rows) || [];
    // Each row is the engagement_index_day_1row FS doc verbatim (PG payload column).
    // Project into the same record shape that processEngagementData produces.
    const DEBI_TYPES = ['DPSRRS', 'OFRS', 'OFES', 'RPRS', 'VAS'];
    const out: Record<string, any> = {};
    for (const data of rows) {
      if (!data || !data.email) continue;
      for (const type of DEBI_TYPES) {
        const key = `${data.company}-${data.email}-${type}-${data.date}`;
        out[key] = {
          company: data.company,
          email: data.email,
          total: data[`${type}_total`] || 0,
          answer: data[`${type}_answer`] || 0,
          date: data.date,
          type,
          team: data.team,
        };
      }
    }
    return out;
  } catch (e) {
    console.error('[engagementPgApi] pgEngagementIndexDay failed:', e);
    return null;
  }
}
